import { ReferenceConverter } from '../actions/formula';
import { getCellByColAndRowIndex, getColumnLetter, parentsUntil } from '../base/util';
import * as literals from '../base/string-literals';
import * as events from '../base/constant';
import { EventHandler, isNullOrUndefined } from '@syncfusion/ej2-base';
/**
 * `FormulaCellEditor` provides a custom cell editor for formula cell inputs in EJ2 Grid.
 *
 * @hidden
 */
var FormulaCellEditor = /** @class */ (function () {
    function FormulaCellEditor(parent) {
        this.editableDiv = null;
        this.currentValue = '';
        this.displayValue = '';
        this.lastFormulaCaretPosition = 0;
        this.highlightedCells = [];
        this.referenceToColorMap = new Map(); // Maps cell reference to color
        this.currentEditingRowNum = 1;
        this.parent = parent;
    }
    /**
     * Register event listeners for cell edit mode.
     *
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.addEventListener = function () {
        if (this.parent.isDestroyed) {
            return;
        }
        if (this.editableDiv) {
            EventHandler.add(this.editableDiv, 'click', this.onEditableDivClick, this);
            EventHandler.add(this.editableDiv, 'focusin', this.onEditableDivFocusIn, this);
            EventHandler.add(this.editableDiv, 'focusout', this.onEditableDivFocusOut, this);
            EventHandler.add(this.editableDiv, 'input', this.onInputChange, this);
        }
        EventHandler.add(this.parent.element, 'click', this.onGridCellClick, this);
        this.parent.on(events.destroy, this.destroy, this);
    };
    /**
     * Remove event listeners (called during destroy).
     *
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.removeEventListener = function () {
        if (this.parent.isDestroyed) {
            return;
        }
        if (this.editableDiv) {
            EventHandler.remove(this.editableDiv, 'click', this.onEditableDivClick);
            EventHandler.remove(this.editableDiv, 'focusin', this.onEditableDivFocusIn);
            EventHandler.remove(this.editableDiv, 'focusout', this.onEditableDivFocusOut);
            EventHandler.remove(this.editableDiv, 'input', this.onInputChange);
        }
        EventHandler.remove(this.parent.element, 'click', this.onGridCellClick);
        this.parent.off(events.destroy, this.destroy);
    };
    FormulaCellEditor.prototype.create = function (args) {
        this.editableDiv = document.createElement('span');
        this.editableDiv.contentEditable = 'true';
        this.editableDiv.className = 'e-field e-control e-formula-edit e-lib e-input-group e-control-wrapper e-valid-input';
        if (args.column.textAlign) {
            this.editableDiv.style.textAlign = args.column.textAlign;
        }
        this.addEventListener();
        return this.editableDiv;
    };
    FormulaCellEditor.prototype.read = function (element) {
        var userInput = element && element.textContent ? element.textContent.trim() : '';
        if (userInput && userInput.startsWith('=')) {
            var converted = this.convertCellReferencesToREF(userInput, this.currentEditingRowNum);
            return converted;
        }
        return userInput || '';
    };
    FormulaCellEditor.prototype.write = function (args) {
        var _this = this;
        if (this.editableDiv && args) {
            var rowData = args.rowData;
            var value = rowData[args.column.field];
            this.currentValue = value !== undefined && value !== null ? value.toString() : '';
            var primaryKeyField = this.parent.getPrimaryKeyFieldNames()[0];
            var visualRowIndex = this.parent.getRowIndexByPrimaryKey(rowData["" + primaryKeyField]);
            if (this.parent.allowPaging && this.parent.pageSettings) {
                visualRowIndex = ((this.parent.pageSettings.currentPage - 1) * this.parent.pageSettings.pageSize) + visualRowIndex;
            }
            this.currentEditingRowNum = visualRowIndex + 1;
            if (this.currentValue.startsWith('=')) {
                this.displayValue = this.convertFormulaToCellReference(this.currentValue, visualRowIndex);
            }
            else {
                this.displayValue = this.currentValue;
            }
            this.renderColorizedFormula(this.displayValue);
            this.updateReferenceHighlight();
            if (args.requestType === 'beginEdit') {
                requestAnimationFrame(function () {
                    requestAnimationFrame(function () {
                        if (_this.editableDiv) {
                            _this.editableDiv.focus();
                            _this.editableDiv.classList.add('e-input-focus');
                            _this.setCursorPosition(_this.displayValue.length);
                        }
                    });
                });
            }
        }
    };
    /**
     * Renders the formula with colored spans for each cell reference inside the editable div
     *
     * @param {string} formula The formula string to render
     * @param {number} cursorPosition Text cursor position
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.renderColorizedFormula = function (formula, cursorPosition) {
        var _this = this;
        if (!this.editableDiv) {
            return;
        }
        this.editableDiv.innerHTML = '';
        var references = this.extractFormulaReferencesWithPositions(formula);
        if (references.length === 0) {
            this.editableDiv.textContent = formula;
            if (cursorPosition) {
                this.setCursorPosition(cursorPosition);
            }
            return;
        }
        var lastIndex = 0;
        this.referenceToColorMap.clear();
        references.forEach(function (ref) {
            if (lastIndex < ref.startPos) {
                var textBefore = document.createTextNode(formula.substring(lastIndex, ref.startPos));
                _this.editableDiv.appendChild(textBefore);
            }
            var span;
            if (_this.isAutoFillFormula()) {
                var refUpper = ref.text.toUpperCase();
                if (!_this.referenceToColorMap.has(refUpper)) {
                    var colorIndex = _this.referenceToColorMap.size % 7;
                    _this.referenceToColorMap.set(refUpper, colorIndex + 1);
                }
                var classIndex = _this.referenceToColorMap.get(refUpper);
                span = _this.parent.createElement('span', { className: "e-formula-token e-formula-token-" + classIndex });
            }
            else {
                span = _this.parent.createElement('span', { className: 'e-formula-token' });
            }
            span.textContent = ref.text;
            _this.editableDiv.appendChild(span);
            lastIndex = ref.endPos;
        });
        if (lastIndex < formula.length) {
            var textAfter = document.createTextNode(formula.substring(lastIndex));
            this.editableDiv.appendChild(textAfter);
        }
        if (cursorPosition) {
            this.setCursorPosition(cursorPosition);
        }
    };
    /**
     * Extracts formula references with their start and end positions.
     *
     * @param {string} formula - The formula string.
     * @returns {Array} Array of references with positions.
     * @hidden
     */
    FormulaCellEditor.prototype.extractFormulaReferencesWithPositions = function (formula) {
        var references = [];
        // eslint-disable-next-line security/detect-unsafe-regex
        var refRegex = /\$?[A-Z]{1,3}\$?\d+(?::\$?[A-Z]{1,3}\$?\d+)?(?!\()/gi;
        var match = refRegex.exec(formula);
        while (match !== null) {
            references.push({ text: match[0], startPos: match.index, endPos: match.index + match[0].length });
            match = refRegex.exec(formula);
        }
        return references;
    };
    /**
     * Converts a formula from simple cell reference format (e.g., C6*D6) back to REF(COLUMN("fieldname"), ROW(n)) format.
     *
     * @param {string} formula - The formula string in simple format to convert
     * @param {number} currentRowNum - The current row number being edited (1-based) for detecting cross-row references
     * @returns {string} The converted formula with REF function calls
     * @hidden
     */
    FormulaCellEditor.prototype.convertCellReferencesToREF = function (formula, currentRowNum) {
        var _this = this;
        var columns = this.parent.getColumns();
        var columnLetterToField = {};
        for (var i = 0; i < columns.length; i++) {
            var col = columns[parseInt(i.toString(), 10)];
            if (col.field) {
                columnLetterToField[getColumnLetter(i)] = col.field;
            }
        }
        formula = formula.toUpperCase();
        return formula.replace(/(\$?)([A-Z]+)(\$?)(\d+)/gi, function (match, colDollar, columnLetter, rowDollar, rowNum) {
            // eslint-disable-next-line security/detect-object-injection
            var fieldName = columnLetterToField[columnLetter];
            if (fieldName) {
                var refRowNum = parseInt(rowNum, 10);
                var dataSource = _this.parent.getDataModule().
                    dataManager.executeLocal(_this.parent.getDataModule().generateQuery(true));
                var primaryKeyField = _this.parent.getPrimaryKeyFieldNames()[0];
                var rowIndex = parseInt(rowNum.toString(), 10);
                rowIndex = dataSource[rowIndex - 1]["" + primaryKeyField];
                if (rowDollar === '$') {
                    rowIndex = "$" + rowIndex;
                }
                else if (!isNullOrUndefined(currentRowNum) && refRowNum !== currentRowNum) {
                    rowIndex = "$" + rowIndex;
                }
                var colPrefix = colDollar === '$' ? '$' : '';
                return "REF(COLUMN(\"" + colPrefix + fieldName + "\"),ROW(" + rowIndex + "))";
            }
            return match;
        });
    };
    /**
     * Converts a formula from REF(COLUMN("fieldname"), ROW(n)) format to simple cell reference format (e.g., C6).
     *
     * @param {string} formula - The formula string to convert
     * @param {number} visualRowIndex - The visual row index in the current sorted/filtered view (optional)
     * @returns {string} The converted formula with simple cell references for editing display
     * @hidden
     */
    FormulaCellEditor.prototype.convertFormulaToCellReference = function (formula, visualRowIndex) {
        var _this = this;
        var columns = this.parent.getColumns();
        var fieldToColumnLetter = {};
        for (var i = 0; i < columns.length; i++) {
            var col = columns[parseInt(i.toString(), 10)];
            if (col.field) {
                fieldToColumnLetter[col.field] = getColumnLetter(i);
            }
        }
        return formula.replace(/REF\(COLUMN\(["']([^"']+)["']\),ROW\((\$*?)(\d+)\)\)/gi, function (match, fieldName, dollarPrefix, rowNum) {
            var hasAbsoluteColumn = fieldName.charAt(0) === '$';
            var cleanFieldName = hasAbsoluteColumn ? fieldName.substring(1) : fieldName;
            // eslint-disable-next-line security/detect-object-injection
            var columnLetter = fieldToColumnLetter[cleanFieldName];
            var currentRowNum = visualRowIndex !== undefined ? visualRowIndex + 1 : 1;
            var dataSource = _this.parent.getDataModule().
                dataManager.executeLocal(_this.parent.getDataModule().generateQuery(true));
            var primaryKeyField = _this.parent.getPrimaryKeyFieldNames()[0];
            var rowValue = parseInt(rowNum.toString(), 10);
            var primaryKeyValue = dataSource.findIndex(function (item) { return item["" + primaryKeyField] === rowValue; });
            if (primaryKeyValue === -1) {
                return match;
            }
            var rowIndex = primaryKeyValue + 1;
            if (columnLetter) {
                if (dollarPrefix === '$') {
                    return hasAbsoluteColumn ? "$" + columnLetter + "$" + rowIndex : "" + columnLetter + rowIndex;
                }
                else {
                    return "" + columnLetter + currentRowNum;
                }
            }
            return match;
        });
    };
    /**
     * Handles click event on the editable div element.
     *
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.onEditableDivClick = function () {
        if (this.editableDiv) {
            this.editableDiv.focus();
            this.editableDiv.classList.add('e-input-focus');
            var curPosition = this.getCursorPositionInFormula();
            this.setCursorPosition(curPosition);
        }
    };
    FormulaCellEditor.prototype.onEditableDivFocusIn = function (e) {
        if (this.editableDiv && !isNullOrUndefined(parentsUntil(e.target, literals.addedRow))) {
            this.editableDiv.classList.add('e-input-focus');
        }
    };
    FormulaCellEditor.prototype.onEditableDivFocusOut = function (e) {
        if (this.editableDiv && !isNullOrUndefined(parentsUntil(e.target, literals.addedRow))) {
            this.editableDiv.classList.remove('e-input-focus');
        }
    };
    /**
     * Handles input change event on the editable div element.
     *
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.onInputChange = function () {
        var _this = this;
        var formula = this.editableDiv ? this.editableDiv.textContent : '';
        this.displayValue = formula;
        var savedCursorPos = this.getCursorPositionInFormula();
        requestAnimationFrame(function () {
            requestAnimationFrame(function () {
                _this.renderColorizedFormula(formula, savedCursorPos);
                _this.updateReferenceHighlight();
            });
        });
    };
    /**
     * Extracts cell references from a display string with their positions.
     *
     * @param {string} display - The display string to extract references from.
     * @returns {Array} Array of references with their text and positions.
     * @hidden
     */
    FormulaCellEditor.prototype.extractRefsFromDisplay = function (display) {
        var cleaned = display.replace(/"(?:\\.|[^"\\])*"/g, '');
        // eslint-disable-next-line security/detect-unsafe-regex
        var refRegex = /\$?[A-Z]+\$?\d+(?::\$?[A-Z]+\$?\d+)?/gi;
        var results = [];
        var match = refRegex.exec(cleaned);
        while (match !== null) {
            results.push({ text: match[0], start: match.index, end: match.index + match[0].length });
            match = refRegex.exec(cleaned);
        }
        return results;
    };
    /**
     * Handles click event on grid cells for formula cell reference selection.
     *
     * @param {MouseEvent} e - The mouse event.
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.onGridCellClick = function (e) {
        var _this = this;
        var target = e.target;
        if (this.isAutoFillFormula() && target.classList.contains('e-rowcell') && this.parent.isEdit && this.editableDiv) {
            var cellElement = parentsUntil(target, literals.rowCell);
            var rowElement = parentsUntil(cellElement, literals.row);
            var rowIndex = parseInt(rowElement.getAttribute(literals.ariaRowIndex), 10) - 1;
            var colIndex = parseInt(cellElement.getAttribute(literals.ariaColIndex), 10) - 1;
            var formulaIndex = this.parent.allowPaging && this.parent.pageSettings ?
                ((this.parent.pageSettings.currentPage - 1) * this.parent.pageSettings.pageSize) + rowIndex : rowIndex;
            var clickedRef_1 = getColumnLetter(colIndex) + (formulaIndex + 1);
            var currentDisplay = this.editableDiv.textContent;
            var caret_1 = this.getCursorPositionInFormula();
            var refs = this.extractRefsFromDisplay(currentDisplay);
            if (!refs.length) {
                this.parent.endEdit();
                return;
            }
            var tokenIndex = -1;
            for (var i = 0; i < refs.length; i++) {
                if (caret_1 >= refs[parseInt(i.toString(), 10)].start && caret_1 <= refs[parseInt(i.toString(), 10)].end) {
                    tokenIndex = i;
                    break;
                }
            }
            if (tokenIndex === -1) {
                var prevChar = currentDisplay.charAt(Math.max(0, caret_1 - 1));
                var nextChar = currentDisplay.charAt(caret_1);
                var isEndPosition = caret_1 === currentDisplay.length;
                var isAfterOperator = /[+\-*/×÷(,]/.test(prevChar);
                var isInsideToken = /[A-Za-z0-9_$"]/.test(prevChar) || /[A-Za-z0-9_$"]/.test(nextChar);
                if (!isEndPosition && !isAfterOperator && isInsideToken) {
                    this.parent.endEdit();
                    return;
                }
                var newDisplay_1 = currentDisplay.slice(0, caret_1) + clickedRef_1 + currentDisplay.slice(caret_1);
                this.currentValue = newDisplay_1;
                this.displayValue = newDisplay_1;
                this.renderColorizedFormula(newDisplay_1);
                this.updateReferenceHighlight();
                requestAnimationFrame(function () {
                    _this.setCursorPosition(caret_1 + clickedRef_1.length);
                });
                return;
            }
            // eslint-disable-next-line security/detect-object-injection
            var token = refs[tokenIndex];
            var newDisplay = void 0;
            if (token.text.indexOf(':') !== -1) {
                var _a = token.text.split(':'), left = _a[0], right = _a[1];
                var leftEnd = token.start + left.length;
                if (caret_1 <= leftEnd) {
                    newDisplay = currentDisplay.slice(0, token.start) + clickedRef_1 + ':' + right + currentDisplay.slice(token.end);
                }
                else {
                    newDisplay = currentDisplay.slice(0, token.start) + left + ':' + clickedRef_1 + currentDisplay.slice(token.end);
                }
            }
            else {
                newDisplay = currentDisplay.slice(0, token.start) + clickedRef_1 + currentDisplay.slice(token.end);
            }
            this.currentValue = newDisplay;
            this.displayValue = newDisplay;
            this.renderColorizedFormula(newDisplay);
            this.updateReferenceHighlight();
            this.lastFormulaCaretPosition = caret_1;
            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    _this.setCursorPosition(caret_1);
                });
            });
        }
    };
    /**
     * Gets the current cursor position within the formula.
     *
     * @returns {number} The cursor position in the formula.
     * @hidden
     */
    FormulaCellEditor.prototype.getCursorPositionInFormula = function () {
        var selection = window.getSelection();
        var range = selection.getRangeAt(0);
        if (!this.editableDiv || !selection || selection.rangeCount === 0 || !this.editableDiv.contains(range.startContainer)) {
            return this.lastFormulaCaretPosition;
        }
        var preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(this.editableDiv);
        preCaretRange.setEnd(range.startContainer, range.startOffset);
        this.lastFormulaCaretPosition = preCaretRange.toString().length;
        return this.lastFormulaCaretPosition;
    };
    /**
     * Sets the cursor position in the formula editor.
     *
     * @param {number} position - The position to set the cursor at.
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.setCursorPosition = function (position) {
        if (!this.editableDiv) {
            return;
        }
        if (this.editableDiv.childNodes.length === 0) {
            this.editableDiv.appendChild(document.createTextNode(''));
        }
        var selection = window.getSelection();
        var range = document.createRange();
        var characterCount = 0;
        var nodeStack = [this.editableDiv];
        var cursorFound = false;
        var currentNode = nodeStack.pop();
        while (!cursorFound && currentNode) {
            if (currentNode.nodeType === Node.TEXT_NODE) {
                var textNode = currentNode;
                var nextCharacterCount = characterCount + textNode.length;
                if (position <= nextCharacterCount) {
                    range.setStart(textNode, position - characterCount);
                    cursorFound = true;
                }
                characterCount = nextCharacterCount;
            }
            else {
                for (var childIndex = currentNode.childNodes.length - 1; childIndex >= 0; childIndex--) {
                    nodeStack.push(currentNode.childNodes[parseInt(childIndex.toString(), 10)]);
                }
            }
            currentNode = nodeStack.pop();
        }
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    };
    /**
     * Updates the highlight for cell references in the formula.
     *
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.updateReferenceHighlight = function () {
        var _this = this;
        if (!this.isAutoFillFormula() || !this.editableDiv) {
            return;
        }
        this.clearReferenceHighlight();
        this.referenceToColorMap.clear();
        var formula = this.editableDiv ? this.editableDiv.textContent : '';
        var references = this.extractFormulaReferences(formula);
        references.forEach(function (ref) {
            var refUpper = ref.toUpperCase();
            if (!_this.referenceToColorMap.has(refUpper)) {
                var colorIndex = _this.referenceToColorMap.size % 7;
                _this.referenceToColorMap.set(refUpper, colorIndex + 1);
            }
            var classIndex = _this.referenceToColorMap.get(refUpper);
            var className = "e-formula-cell-" + classIndex;
            _this.highlightReference(ref, className);
        });
    };
    /**
     * Extracts unique cell references from a formula string.
     *
     * @param {string} formula - The formula string to extract references from.
     * @returns {string[]} Array of unique cell references.
     * @hidden
     */
    FormulaCellEditor.prototype.extractFormulaReferences = function (formula) {
        // eslint-disable-next-line security/detect-unsafe-regex
        var refRegex = /\$?[A-Z]+\$?\d+(?::\$?[A-Z]+\$?\d+)?/gi;
        var matches = formula.match(refRegex);
        if (!matches) {
            return [];
        }
        var uniqueRefs = [];
        matches.forEach(function (ref) {
            var normalized = ref.toUpperCase();
            if (uniqueRefs.indexOf(normalized) === -1) {
                uniqueRefs.push(normalized);
            }
        });
        return uniqueRefs;
    };
    /**
     * Clears all reference highlighting from cells.
     *
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.clearReferenceHighlight = function () {
        this.highlightedCells.forEach(function (cell) {
            var _a;
            var classesToRemove = Array.from(cell.classList).filter(function (cls) {
                return cls.startsWith('e-formula-cell-') ||
                    cls.startsWith('e-formula-range') ||
                    cls.startsWith('e-formula-border-range-');
            });
            (_a = cell.classList).remove.apply(_a, classesToRemove);
        });
        this.highlightedCells = [];
    };
    FormulaCellEditor.prototype.getPageRowIndex = function (index) {
        if (this.parent.allowPaging && this.parent.pageSettings) {
            return index - ((this.parent.pageSettings.currentPage - 1) * this.parent.pageSettings.pageSize);
        }
        return index;
    };
    /**
     * Highlights a cell reference or range in the grid.
     *
     * @param {string} reference - The cell reference or range to highlight.
     * @param {string} formulaBorder - The CSS class for the border style.
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.highlightReference = function (reference, formulaBorder) {
        if (reference.indexOf(':') >= 0) {
            var _a = reference.split(':'), startReference = _a[0], endReference = _a[1];
            var startCellReference = ReferenceConverter.parseReference(startReference);
            var endCellReference = ReferenceConverter.parseReference(endReference);
            var startIndex = this.getPageRowIndex(startCellReference.row);
            var endIndex = this.getPageRowIndex(endCellReference.row);
            var minRowIndex = Math.min(startIndex, endIndex);
            var maxRowIndex = Math.max(startIndex, endIndex);
            var minColumnIndex = Math.min(startCellReference.col, endCellReference.col);
            var maxColumnIndex = Math.max(startCellReference.col, endCellReference.col);
            this.highlightRangeBorder(minRowIndex, maxRowIndex, minColumnIndex, maxColumnIndex, formulaBorder);
        }
        else {
            var parsed = ReferenceConverter.parseReference(reference);
            this.highlightCell(parsed.col, this.getPageRowIndex(parsed.row), formulaBorder);
        }
    };
    /**
     * Highlights a single cell in the grid.
     *
     * @param {number} colIndex - The column index of the cell.
     * @param {number} rowIndex - The row index of the cell.
     * @param {string} formulaBorder - The CSS class for the border style.
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.highlightCell = function (colIndex, rowIndex, formulaBorder) {
        var columns = this.parent.getColumns();
        if (colIndex < 0 || colIndex >= columns.length) {
            return;
        }
        var rowElement = this.parent.getRowByIndex(rowIndex);
        if (!rowElement) {
            return;
        }
        var cellElement = getCellByColAndRowIndex(this.parent, columns[parseInt(colIndex.toString(), 10)], rowIndex, colIndex);
        if (cellElement) {
            cellElement.classList.add(formulaBorder);
            this.highlightedCells.push(cellElement);
        }
    };
    /**
     * Create a single border div around an entire range of cells
     *
     * @param {number} minRow Start row index
     * @param {number} maxRow End row index
     * @param {number} minCol Start column index
     * @param {number} maxCol End column index
     * @param {string} formulaBorder CSS class for the border
     * @returns {void}
     * @hidden
     */
    FormulaCellEditor.prototype.highlightRangeBorder = function (minRow, maxRow, minCol, maxCol, formulaBorder) {
        var columns = this.parent.getColumns();
        if (minCol < 0 || maxCol >= columns.length || minRow < 0 || maxRow >= this.parent.currentViewData.length) {
            return;
        }
        for (var row = minRow; row <= maxRow; row++) {
            for (var col = minCol; col <= maxCol; col++) {
                var cell = getCellByColAndRowIndex(this.parent, columns[parseInt(col.toString(), 10)], row, col);
                if (!cell) {
                    continue;
                }
                var borderClass = formulaBorder.replace('e-formula-cell-', 'e-formula-border-range-');
                cell.classList.add('e-formula-range', borderClass);
                if (row === minRow) {
                    cell.classList.add('e-formula-range-top');
                }
                if (row === maxRow) {
                    cell.classList.add('e-formula-range-bottom');
                }
                if (col === minCol) {
                    cell.classList.add('e-formula-range-left');
                }
                if (col === maxCol) {
                    cell.classList.add('e-formula-range-right');
                }
                this.highlightedCells.push(cell);
            }
        }
    };
    /**
     * Determines if auto-fill formula highlighting should be enabled.
     *
     * @returns {boolean} True if auto-fill formula is enabled, false otherwise.
     * @hidden
     */
    FormulaCellEditor.prototype.isAutoFillFormula = function () {
        var selectionSettings = this.parent.selectionSettings;
        var cellSelectionMode = selectionSettings.cellSelectionMode;
        var result = this.parent.enableAutoFill === true && cellSelectionMode.indexOf('Box') > -1 &&
            selectionSettings.mode === 'Cell' && selectionSettings.type === 'Multiple';
        return result;
    };
    FormulaCellEditor.prototype.destroy = function () {
        this.removeEventListener();
        this.clearReferenceHighlight();
        this.editableDiv = null;
        this.displayValue = '';
        this.currentValue = '';
        this.referenceToColorMap.clear();
        this.highlightedCells = [];
    };
    return FormulaCellEditor;
}());
export { FormulaCellEditor };
